import axios, { AxiosRequestConfig } from 'axios';
import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';

type LoaderContextProps = { isLoading: boolean; forceLoad: (value: boolean) => void };

const LoaderContext = createContext<LoaderContextProps>({ isLoading: false, forceLoad: () => {} });

const excludedApiUrlsForLoader: string[] = [
  '/assets/blobstorage',
  '/restservices/portal/user-notifications',
];

const isExcludedUrl = (url: string): boolean =>
  excludedApiUrlsForLoader.some((pathPortion) => url.includes(pathPortion));

export const LoaderProvider = ({ children }: PropsWithChildren<any>) => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const showMainLoader = () => {
      setIsLoading(true);
      document.body.classList.toggle('loading', true);
    };

    const hideMainLoader = () => {
      setIsLoading(false);
      document.body.classList.toggle('loading', false);
    };

    const requestInterceptor = axios.interceptors.request.use((config: AxiosRequestConfig) => {
      if (!isExcludedUrl(config.url || '')) {
        showMainLoader();
      }
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (config: AxiosRequestConfig) => {
        hideMainLoader();
        return config;
      },
      (error: any) => {
        hideMainLoader();
        return Promise.reject(error);
      },
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const forceLoad = (vaue: boolean) => setIsLoading(vaue);

  return (
    <LoaderContext.Provider value={{ isLoading, forceLoad }}>{children}</LoaderContext.Provider>
  );
};

export const useLoader = () => {
  return useContext(LoaderContext);
};
